import React from 'react'
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Divider,
  Grid,
  Box,
  Chip,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Autocomplete,
  CircularProgress,
  InputAdornment,
  IconButton,
  Button,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import VenafiCertificatePreview from './VenafiCertificatePreview'
import PreExistingCertFields from './PreExistingCertFields'

const VenafiCertLookupStep = ({
  formData,
  setFormData,
  isComplete,
  checkVenafiCommonName,
  venafiLookup,
  selectVenafiMatch,
  recheckVenafiMatch,
  itsmProducts,
  loadingProducts,
  productSearchText,
  setProductSearchText,
  selectedProduct,
  handleProductChange,
  stepNumber = 2,
}) => {
  const hasSelectedMatch = !!venafiLookup.selectedMatch
  const venafiEnv = venafiLookup.selectedMatch?.environment || null
  const venafiAppName = venafiLookup.selectedMatch?.application_name || null
  const venafiRequestorName = venafiLookup.selectedMatch?.requestor || null

  // Determine if the app name from Venafi matches an ITSM product
  const matchedProduct = venafiAppName
    ? itsmProducts.find(
        (p) =>
          p.product_name?.toLowerCase() === venafiAppName.toLowerCase() ||
          p.product_code?.toLowerCase() === venafiAppName.toLowerCase()
      )
    : null

  return (
    <Collapse in={formData.preExisting === 'true'}>
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={isComplete ? '✓' : String(stepNumber)}
              color={isComplete ? 'success' : 'primary'}
              size="small"
            />
            Venafi Certificate Lookup
          </Typography>
          <Divider sx={{ my: 2 }} />

          <Grid container spacing={3}>
            {/* Common Name search */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  label="Common Name *"
                  value={formData.commonName}
                  onChange={(e) => setFormData({ ...formData, commonName: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && formData.commonName.trim()) {
                      e.preventDefault()
                      checkVenafiCommonName()
                    }
                  }}
                  helperText="Enter the certificate common name, then click Search"
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={checkVenafiCommonName}
                          disabled={!formData.commonName.trim() || venafiLookup.status === 'loading'}
                          edge="end"
                          size="small"
                          title="Search Venafi"
                        >
                          {venafiLookup.status === 'loading' && !venafiLookup.selectedMatch
                            ? <CircularProgress size={18} />
                            : <SearchIcon />
                          }
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  variant="contained"
                  onClick={checkVenafiCommonName}
                  disabled={!formData.commonName.trim() || venafiLookup.status === 'loading'}
                  startIcon={
                    venafiLookup.status === 'loading' && !venafiLookup.selectedMatch
                      ? <CircularProgress size={16} color="inherit" />
                      : <SearchIcon />
                  }
                  sx={{ mt: 0.25, height: 56, whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  Search Venafi
                </Button>
              </Box>
            </Grid>

            {/* Error: certificate already onboarded in CertManage */}
            {venafiLookup.alreadyOnboarded && (
              <Grid item xs={12}>
                <Alert severity="error">
                  {venafiLookup.error || 'This certificate has already been onboarded in CertManage.'}
                </Alert>
              </Grid>
            )}

            {/* Venafi lookup results and certificate selection */}
            {!venafiLookup.alreadyOnboarded && (
              <PreExistingCertFields
                formData={formData}
                setFormData={setFormData}
                venafiLookup={venafiLookup}
                selectVenafiMatch={selectVenafiMatch}
              />
            )}

            {/* Warning: cert not yet active in Venafi */}
            {venafiLookup.certNotActive && hasSelectedMatch && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  This certificate has not been initially renewed and is not active in Venafi yet.
                  Please renew the certificate in Venafi for the first time and then come back to
                  onboard it from CertManage.
                </Alert>
                <Box sx={{ mt: 1.5 }}>
                  <Button
                    variant="outlined"
                    onClick={recheckVenafiMatch}
                    disabled={venafiLookup.status === 'loading'}
                    startIcon={
                      venafiLookup.status === 'loading'
                        ? <CircularProgress size={16} color="inherit" />
                        : <SearchIcon />
                    }
                  >
                    Check Again
                  </Button>
                </Box>
              </Grid>
            )}

            {/* Auto-extracted fields after cert selection */}
            {hasSelectedMatch && !venafiLookup.certNotActive && (
              <>
                {/* Certificate Requestor name */}
                {venafiRequestorName && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Certificate Requestor"
                      value={venafiRequestorName}
                      InputProps={{ readOnly: true }}
                      helperText="Auto-populated from Venafi certificate"
                    />
                  </Grid>
                )}

                {/* Environment */}
                <Grid item xs={12} md={6}>
                  {(() => {
                    const envLabels = {
                      prd: 'Production (PRD)',
                      uat: 'UAT',
                      qa: 'QA',
                      dev: 'Development (DEV)',
                    }
                    const knownEnv = envLabels[venafiEnv]
                    if (knownEnv) {
                      return (
                        <TextField
                          fullWidth
                          label="Environment"
                          value={knownEnv}
                          InputProps={{ readOnly: true }}
                          helperText="Auto-detected from Venafi certificate"
                        />
                      )
                    }
                    // Unknown / missing env — let user pick
                    return (
                      <FormControl fullWidth>
                        <InputLabel>SDLC Environment *</InputLabel>
                        <Select
                          value={formData.environment}
                          onChange={(e) =>
                            setFormData({ ...formData, environment: e.target.value, venafiPolicyBase: '' })
                          }
                          label="SDLC Environment *"
                        >
                          <MenuItem value="dev">Development (DEV)</MenuItem>
                          <MenuItem value="qa">QA</MenuItem>
                          <MenuItem value="uat">UAT</MenuItem>
                          <MenuItem value="prd">Production (PRD)</MenuItem>
                        </Select>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                          Could not auto-detect environment from Venafi. Please select it.
                        </Typography>
                      </FormControl>
                    )
                  })()}
                </Grid>

                {/* Application / Product Name */}
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    fullWidth
                    options={itsmProducts}
                    getOptionLabel={(option) => option?.product_name || ''}
                    value={selectedProduct}
                    onChange={handleProductChange}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === 'input') {
                        setProductSearchText(newInputValue)
                      }
                    }}
                    loading={loadingProducts}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Application / Product Name *"
                        placeholder="Search products..."
                        helperText={
                          matchedProduct
                            ? `Auto-matched from Venafi (${venafiAppName}). You can change this if needed.`
                            : venafiAppName
                              ? `Venafi returned "${venafiAppName}" but it did not match a known product. Please select or search manually.`
                              : 'No application name found in Venafi. Please select a product.'
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingProducts ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.product_code === value.product_code}
                    renderOption={(props, option) => (
                      <li {...props} key={option.product_id}>
                        <Box>
                          <Typography variant="body1">{option.product_name}</Typography>
                          {option.product_code && (
                            <Typography variant="caption" color="text.secondary">
                              Code: {option.product_code}
                            </Typography>
                          )}
                        </Box>
                      </li>
                    )}
                  />
                </Grid>

                {venafiAppName && !matchedProduct && (
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ py: 0.5 }}>
                      Venafi certificate path contained application name <strong>"{venafiAppName}"</strong> which did not
                      match any known ITSM product. Please select the correct product above.
                    </Alert>
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </CardContent>
      </Card>
    </Collapse>
  )
}

export default VenafiCertLookupStep
